import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Backdrop,
  CircularProgress,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Menu as MenuIcon,
  FilterList,
  Refresh,
  Visibility as VisibilityIcon,
  AutoAwesome,
} from '@mui/icons-material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { Logger } from 'utils/Logger';
import { DynamicBreadcrumbs } from 'components/Breadcrumb';
import { apiClient } from 'utils/ApiClient';
import { AIPromptProjectView } from './AIPromptProjectView';
import { RootState } from 'state/store';
import { User } from 'modules/Interfaces/Company';
import { getAnamnesisTileCenterColors } from './anamnesisTileTheme';

export interface AIPromptProjectItem {
  _id: string;
  projectName: string;
  status: 'active' | 'inactive';
  visibility?: 'private' | 'company';
  createdBy?: string;
  updatedAt?: string;
  promptCount: number;
}

const VIEWMODE_STORAGE_KEY = 'anamnesis-ai-prompts-view-mode';
const AI_PROMPTS_LIST_PATH = '/anamnesis/ai-prompts';

export const AIPrompts: React.FC = () => {
  const theme = useTheme();
  const tileCenterColors = getAnamnesisTileCenterColors(theme);
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.authentication.user);
  const { projectName: routeProjectName } = useParams<{ projectName?: string }>();
  const selectedProject = routeProjectName ? decodeURIComponent(routeProjectName) : null;

  const [projects, setProjects] = useState<AIPromptProjectItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [creatorNameById, setCreatorNameById] = useState<Record<string, string>>({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
    const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
    return stored === 'table' ? 'table' : 'tile';
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<string>('updatedAt');
  const [sortOrder, setSortOrder] = useState<string>('desc');
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [scopeFilter, setScopeFilter] = useState<string>('my');

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);

  const showMessage = useCallback((message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'active') params.set('status', statusFilter);
      if (scopeFilter !== 'my') params.set('scope', scopeFilter);
      const query = params.toString();
      const endpoint = `/anamnesis/knowledge-graphs${query ? `?${query}` : ''}`;
      const data = await apiClient.get(endpoint);
      const rows = Array.isArray(data.data) ? data.data : [];
      setProjects(
        rows.map((row: Record<string, unknown>) => ({
          _id: String(row._id ?? ''),
          projectName: String(row.projectName ?? ''),
          status: (row.status === 'inactive' ? 'inactive' : 'active') as 'active' | 'inactive',
          visibility: row.visibility === 'company' ? 'company' : 'private',
          createdBy: row.createdBy ? String(row.createdBy) : undefined,
          updatedAt: row.updatedAt ? String(row.updatedAt) : undefined,
          promptCount: typeof row.promptCount === 'number' ? row.promptCount : Number(row.promptCount) || 0,
        })),
      );
    } catch (error) {
      Logger.error('AIPrompts: failed to fetch projects', error);
      showMessage(`Failed to fetch projects: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, scopeFilter, showMessage]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects, refreshTrigger]);

  useEffect(() => {
    const loadCompanyUsers = async () => {
      const companyId = user?.companyId;
      if (!companyId) return;
      try {
        const data = await apiClient.get(`/user/all/${companyId}`);
        const users = Array.isArray(data.data) ? (data.data as User[]) : [];
        const map: Record<string, string> = {};
        users.forEach((companyUser) => {
          const name = [companyUser.profile?.firstName, companyUser.profile?.lastName].filter(Boolean).join(' ').trim();
          map[String(companyUser._id)] = name || companyUser.email || String(companyUser._id);
        });
        setCreatorNameById(map);
      } catch (error) {
        Logger.error('AIPrompts: failed to fetch company users for Created By labels:', error);
      }
    };
    loadCompanyUsers();
  }, [user?.companyId]);

  const getCreatedByLabel = useCallback((createdBy?: string) => {
    if (!createdBy) return '—';
    if (String(createdBy) === String(user?._id)) {
      const selfName = [user?.profile?.firstName, user?.profile?.lastName].filter(Boolean).join(' ').trim();
      return selfName || user?.email || 'You';
    }
    return creatorNameById[String(createdBy)] || createdBy;
  }, [creatorNameById, user]);

  const filteredAndSortedProjects = useMemo(() => {
    let filtered = projects.filter((project) => {
      const name = project.projectName?.toLowerCase() || '';
      const matchesSearch = name.includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
    filtered.sort((a, b) => {
      let comparison = 0;
      if (sortField === 'projectName') {
        comparison = (a.projectName || '').localeCompare(b.projectName || '');
      } else if (sortField === 'updatedAt') {
        const dA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const dB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        comparison = dA - dB;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    return filtered;
  }, [projects, searchTerm, sortField, sortOrder, statusFilter]);

  const handleViewProject = (projectName: string) => {
    navigate(`${AI_PROMPTS_LIST_PATH}/${encodeURIComponent(projectName)}`);
  };

  if (selectedProject) {
    return <AIPromptProjectView projectName={selectedProject} />;
  }

  const renderVisibilityChip = (visibility?: 'private' | 'company', tile = false) => {
    const isShared = visibility === 'company';
    return (
      <Chip
        label={isShared ? 'Shared' : 'Private'}
        color={isShared ? 'error' : 'success'}
        variant="outlined"
        size="small"
        sx={{
          height: 22,
          fontSize: '0.68rem',
          fontWeight: 700,
          ...(tile ? { boxShadow: '0 1px 4px rgba(0,0,0,0.3)', backgroundColor: 'rgba(255,255,255,0.92)' } : {}),
        }}
      />
    );
  };

  const toolbarSurfaceSx = {
    display: 'flex',
    flexDirection: 'row' as const,
    gap: { xs: 1, md: 2 },
    alignItems: 'center',
    mb: 3,
    p: 1.5,
    borderRadius: 2,
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
  };

  const renderToolbarControls = (variant: 'desktop' | 'mobile-dialog' = 'desktop') => (
    <>
      <TextField
        label="Search AI Prompts"
        variant="outlined"
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        fullWidth={variant === 'mobile-dialog'}
        sx={variant === 'desktop' ? { flexGrow: 1, minWidth: '200px' } : undefined}
      />
      <FormControl fullWidth={variant === 'mobile-dialog'} sx={variant === 'desktop' ? { minWidth: 170 } : undefined} size="small">
        <InputLabel>Sort By</InputLabel>
        <Select value={sortField} label="Sort By" onChange={(e) => setSortField(e.target.value)}>
          <MenuItem value="updatedAt">Last Updated On</MenuItem>
          <MenuItem value="projectName">Project Name</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth={variant === 'mobile-dialog'} sx={variant === 'desktop' ? { minWidth: 110 } : undefined} size="small">
        <InputLabel>Order</InputLabel>
        <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value)}>
          <MenuItem value="asc">ASC</MenuItem>
          <MenuItem value="desc">DSC</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth={variant === 'mobile-dialog'} sx={variant === 'desktop' ? { minWidth: 130 } : undefined} size="small">
        <InputLabel>Status</InputLabel>
        <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth={variant === 'mobile-dialog'} sx={variant === 'desktop' ? { minWidth: 150 } : undefined} size="small">
        <InputLabel>Projects</InputLabel>
        <Select value={scopeFilter} label="Projects" onChange={(e) => setScopeFilter(e.target.value)}>
          <MenuItem value="my">My Projects</MenuItem>
          <MenuItem value="all">All Projects</MenuItem>
        </Select>
      </FormControl>
      <Box display="flex" alignItems="center" gap={1}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_e, newMode) => {
            if (newMode !== null) {
              setViewMode(newMode);
              localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
            }
          }}
          size="small"
          sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
        >
          <ToggleButton value="tile" aria-label="Tile view">
            <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
          </ToggleButton>
          <ToggleButton value="table" aria-label="Table view">
            <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Tooltip title="Refresh">
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setRefreshTrigger((prev) => prev + 1)}
          size="small"
          sx={{ minWidth: 36, px: 1, flexShrink: 0 }}
          aria-label="Refresh"
        >
          <Refresh fontSize="small" />
        </Button>
      </Tooltip>
    </>
  );

  return (
    <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
      {loading && (
        <Backdrop sx={{ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 }} open={loading}>
          <CircularProgress color="inherit" />
        </Backdrop>
      )}

      <DynamicBreadcrumbs items={[
        { itemPath: '/dashboard', itemLabel: 'Home' },
        { itemPath: AI_PROMPTS_LIST_PATH, itemLabel: 'AI Prompts' },
      ]} />

      <Box sx={toolbarSurfaceSx}>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <IconButton
            color="secondary"
            onClick={() => setMobileMenuOpen(true)}
            size="large"
            sx={{ border: '1px solid', borderColor: 'secondary.main', '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' } }}
          >
            <FilterList />
          </IconButton>
        </Box>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
          <IconButton
            color="secondary"
            onClick={() => setToolbarMenuOpen(true)}
            size="large"
            sx={{ border: '1px solid', borderColor: 'secondary.main', '&:hover': { backgroundColor: 'action.hover', borderColor: 'secondary.dark' } }}
          >
            <MenuIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexGrow: 1,
            flexWrap: 'nowrap',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <TextField
            label="Search AI Prompts"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, minWidth: '220px', flexShrink: 1 }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', flexShrink: 0 }}>
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Sort By</InputLabel>
              <Select value={sortField} label="Sort By" onChange={(e) => setSortField(e.target.value)}>
                <MenuItem value="updatedAt">Last Updated On</MenuItem>
                <MenuItem value="projectName">Project Name</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 110 }}>
              <InputLabel>Order</InputLabel>
              <Select value={sortOrder} label="Order" onChange={(e) => setSortOrder(e.target.value)}>
                <MenuItem value="asc">ASC</MenuItem>
                <MenuItem value="desc">DSC</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Projects</InputLabel>
              <Select value={scopeFilter} label="Projects" onChange={(e) => setScopeFilter(e.target.value)}>
                <MenuItem value="my">My Projects</MenuItem>
                <MenuItem value="all">All Projects</MenuItem>
              </Select>
            </FormControl>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_e, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                  localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
                }
              }}
              size="small"
              sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
            >
              <ToggleButton value="tile" aria-label="Tile view">
                <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
              </ToggleButton>
              <ToggleButton value="table" aria-label="Table view">
                <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Refresh">
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setRefreshTrigger((prev) => prev + 1)}
                size="small"
                sx={{ minWidth: 36, px: 1, flexShrink: 0 }}
                aria-label="Refresh"
              >
                <Refresh fontSize="small" />
              </Button>
            </Tooltip>
          </Box>
        </Box>
      </Box>

      <Dialog open={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Search &amp; Filters</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {renderToolbarControls('mobile-dialog')}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMobileMenuOpen(false)} variant="outlined" color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={toolbarMenuOpen} onClose={() => setToolbarMenuOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Actions</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_e, newMode) => {
                if (newMode !== null) {
                  setViewMode(newMode);
                  localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
                }
              }}
              size="small"
              sx={{ '& .MuiToggleButton-root': { border: '1px solid', borderColor: 'divider' } }}
            >
              <ToggleButton value="tile" aria-label="Tile view">
                <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
              </ToggleButton>
              <ToggleButton value="table" aria-label="Table view">
                <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
            <Tooltip title="Refresh">
              <Button
                variant="contained"
                color="secondary"
                onClick={() => { setRefreshTrigger((prev) => prev + 1); setToolbarMenuOpen(false); }}
                size="small"
                sx={{ minWidth: 36, px: 1, alignSelf: 'flex-start' }}
                aria-label="Refresh"
              >
                <Refresh fontSize="small" />
              </Button>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToolbarMenuOpen(false)} variant="outlined" color="secondary">Close</Button>
        </DialogActions>
      </Dialog>

      {viewMode === 'tile' && (
        <Box display="flex" flexWrap="wrap" gap={2.5} sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}>
          {filteredAndSortedProjects.map((project) => (
            <Box
              key={project._id}
              onClick={() => handleViewProject(project.projectName)}
              sx={{
                width: 260,
                height: 200,
                borderRadius: 2.5,
                overflow: 'hidden',
                position: 'relative',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                cursor: 'pointer',
                flexShrink: 0,
                border: '1px solid',
                borderColor: 'divider',
                '&:hover .tile-overlay': { height: 120 },
                '&:hover .tile-details': { opacity: 1 },
                '&:hover .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                '@media (hover: none)': {
                  '& .tile-overlay': { height: 120 },
                  '& .tile-details': { opacity: 1 },
                  '& .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                },
              }}
            >
              <Box sx={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                background: theme.palette.dashboard?.counterHeroGradient ?? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
              }}>
                <AutoAwesome sx={{ fontSize: 40, color: tileCenterColors.foreground, opacity: 0.85 }} />
                <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: tileCenterColors.foreground, lineHeight: 1, textShadow: tileCenterColors.textShadow }}>
                  {project.promptCount} {project.promptCount === 1 ? 'prompt' : 'prompts'}
                </Typography>
              </Box>

              <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 2, display: 'flex', gap: 0.5 }}>
                <Chip
                  label={project.status}
                  color={project.status === 'active' ? 'success' : 'warning'}
                  size="small"
                  sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', textTransform: 'capitalize' }}
                />
                {renderVisibilityChip(project.visibility, true)}
              </Box>

              <Box
                className="tile-overlay"
                sx={{
                  position: 'absolute', bottom: 0, left: 0, right: 0,
                  overflow: 'hidden',
                  background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
                  transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                  height: 40, px: 1.5,
                  display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                }}
              >
                <Typography variant="subtitle2" noWrap className="tile-name-short" sx={{
                  color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                  lineHeight: '40px', textShadow: '0 1px 3px rgba(0,0,0,0.4)', flexShrink: 0,
                  transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
                }}>
                  {project.projectName}
                </Typography>
                <Box className="tile-details" sx={{
                  opacity: 0, transition: 'opacity 0.3s ease 0.08s',
                  display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.25, pb: 1, flex: 1,
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.3, textShadow: '0 1px 3px rgba(0,0,0,0.4)', wordBreak: 'break-word' }}>
                    {project.projectName}
                  </Typography>
                  {project.updatedAt && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                      Updated: {new Date(project.updatedAt).toLocaleDateString()}
                    </Typography>
                  )}
                  {project.visibility === 'company' && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                      Created by: {getCreatedByLabel(project.createdBy)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
          {filteredAndSortedProjects.length === 0 && (
            <Box width="100%" display="flex" justifyContent="center" alignItems="center" py={6}>
              <Typography variant="body2" color="text.secondary">No projects found.</Typography>
            </Box>
          )}
        </Box>
      )}

      {viewMode === 'table' && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.paper', p: 2 }}>
          <TableContainer component={Paper} elevation={0}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Project Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Prompts</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Visibility</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredAndSortedProjects.map((project) => (
                  <TableRow key={project._id} hover>
                    <TableCell>{project.projectName}</TableCell>
                    <TableCell>{project.promptCount}</TableCell>
                    <TableCell>
                      <Chip
                        label={project.status}
                        color={project.status === 'active' ? 'success' : 'warning'}
                        variant="outlined"
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      {renderVisibilityChip(project.visibility)}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View">
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          onClick={() => handleViewProject(project.projectName)}
                          sx={{ minWidth: 36, px: 1 }}
                          aria-label="View"
                        >
                          <VisibilityIcon fontSize="small" />
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredAndSortedProjects.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">No projects found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AIPrompts;
